import { resources } from "@constants/hardcoded-strings";
import { templateAvailabilityLevelOptions } from "@constants/template-availability-level-options";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group"
import { OrganizationSelectFormGroup, RelyingPartySelectFormGroup } from "@containers/common/select-form-groups";
import { LabelWidthTypes } from "@enums/label-width-types";
import { TemplateAvailabilityLevelTypes } from "@enums/template-availability-level-types";
import { SelectOption } from "@iddataweb/idw-common-ui/types"
import { MessagingTemplateGeneralInfoValidationErrors } from "@idw-types/common/validation/messaging-template/messaging-template-validation-errors";
import { IDWSession } from "@idw-types/data/idw-session";
import { FormattedMessagingTemplate } from "@idw-types/data/messaging-template";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { isAxnAdminUser } from "@utils/permissions-util";
import { mapEntityWithNameAndIdToSelectOption } from "@utils/select-option-mapping-util";
import cloneDeep from "lodash/cloneDeep";
import { useMemo } from "react";

type TemplateAvailabilityLevelSectionProps = {
    isDisabled: boolean;
    session: IDWSession;
    state: FormattedMessagingTemplate;
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    labelWidth: LabelWidthTypes;
    isVericationServiceDetailsPage: boolean;
    serviceRelyingPartyId: string;
    serviceOrganizationId: string;
    validationErrors: MessagingTemplateGeneralInfoValidationErrors;
    setState: (state: FormattedMessagingTemplate) => void;
}

export const TemplateAvailabilityLevelSection = (props: TemplateAvailabilityLevelSectionProps) => {
    const { isDisabled, state, axnCompanies, axnRelyingParties, labelWidth, session, isVericationServiceDetailsPage,
        serviceRelyingPartyId, serviceOrganizationId, validationErrors, setState } = props;
    const isRpUser = session.user.axnAdminUserRPProperties.length > 0;

    const handleUpdateTemplateAvailabilityLevel = (selectedOption: SelectOption<TemplateAvailabilityLevelTypes>) => {
        const updatedState = cloneDeep(state);
        if (selectedOption.value === TemplateAvailabilityLevelTypes.GLOBAL) {
            updatedState.company = null;
            updatedState.relyingParty = null;
        } else if (selectedOption.value === TemplateAvailabilityLevelTypes.ORGANIZATION) {
            if (axnCompanies.length === 1) {
                updatedState.company = axnCompanies[0];
            } else if (isVericationServiceDetailsPage && serviceOrganizationId) {
                updatedState.company = axnCompanies.find(company => company.id === serviceOrganizationId);
            }
            updatedState.relyingParty = null;
        } else if (selectedOption.value === TemplateAvailabilityLevelTypes.RELYING_PARTY) {
            if (axnCompanies.length === 1) {
                updatedState.company = axnCompanies[0];
            } else if (isVericationServiceDetailsPage && serviceOrganizationId) {
                updatedState.company = axnCompanies.find(company => company.id === serviceOrganizationId);
            }
            
            if (axnRelyingParties.length === 1) {
                updatedState.relyingParty = axnRelyingParties[0];
            } else if (isVericationServiceDetailsPage && serviceRelyingPartyId) {
                updatedState.relyingParty = axnRelyingParties.find(rp => rp.id === serviceRelyingPartyId);
            }
        }


        setState({
            ...updatedState,
            templateAvailabilityLevel: selectedOption.value
        });
    }

    const handleUpdateCompany = (newValue: SelectOption) => {
        const newCompany = newValue ? axnCompanies.find(company => company.id === newValue.value) : null;
        let newRelyingParty = state.relyingParty;
        if (newCompany && newRelyingParty && newCompany.id !== newRelyingParty.companyID) {
            newRelyingParty = null;
        }
        setState({
            ...state,
            company: newCompany,
            relyingParty: newRelyingParty
        });
    };

    const handleUpdateRelyingParty = (newValue: SelectOption) => {
        const newRelyingParty = newValue ? axnRelyingParties.find(rp => rp.id === newValue.value) : null;
        let company = state.company;
        if (!company && newRelyingParty) {
            const newCompany = axnCompanies.find(company => company.id === newRelyingParty.companyID);
            if (newCompany) {
                company = newCompany;
            }
        }
        setState({
            ...state,
            relyingParty: newRelyingParty,
            company: company
        });
    };

    const isAdminUser = useMemo(() => isAxnAdminUser(session.user), [session.user]);

    const isOrgUser = useMemo(() => {
        const isRpUser = session.user.axnAdminUserRPProperties.length > 0;
        return !isAdminUser && !isRpUser;
    }, [session.user]);

    const possibleTemplateAvailabilityLevelOptions: Array<SelectOption<TemplateAvailabilityLevelTypes>> = useMemo(() => {
        return templateAvailabilityLevelOptions.filter(option => {
            if ((isOrgUser || isRpUser || isVericationServiceDetailsPage) && option.value === TemplateAvailabilityLevelTypes.GLOBAL) {
                return false;
            } else if (isRpUser && option.value === TemplateAvailabilityLevelTypes.ORGANIZATION) {
                return false;
            } else {
                return true;
            }
        });
    }, [session.user, isVericationServiceDetailsPage]);

    const selectedTemplateAvailabilityLevelOption = useMemo(() => {
        if (state.templateAvailabilityLevel) {
            return possibleTemplateAvailabilityLevelOptions.find(level => level.value === state.templateAvailabilityLevel);
        }

        return null;
    }, [possibleTemplateAvailabilityLevelOptions, state.templateAvailabilityLevel]);


    const selectedRelyingParty: SelectOption = useMemo(() => {
        if (state.relyingParty) {
            return mapEntityWithNameAndIdToSelectOption(state.relyingParty);
        }
        return null;
    }, [axnRelyingParties, state.relyingParty]);

    const selectedCompany: SelectOption = useMemo(() => {
        if (state.company) {
            return mapEntityWithNameAndIdToSelectOption(state.company);
        }
        return null;
    }, [axnCompanies, state.company]);

    const availableRelyingParties = useMemo(() => {
        if (state.company) {
            return axnRelyingParties.filter(rp => rp.companyID === state.company.id);
        } else {
            const companyIds = axnCompanies.filter(company => company.type === "RP").map(company => company.id);
            return axnRelyingParties.filter(rp => companyIds.includes(rp.companyID));
        }
    }, [state.company, axnCompanies, axnRelyingParties]);

    const shouldDisableRelyingPartyOption = useMemo(() => {
        return state.relyingParty && isVericationServiceDetailsPage && state.templateAvailabilityLevel === TemplateAvailabilityLevelTypes.RELYING_PARTY;
    }, [session.user, isVericationServiceDetailsPage, state.templateAvailabilityLevel, state.relyingParty]);

    const shouldDisableOrganizationUserOption = useMemo(() => {
        return state.company && isVericationServiceDetailsPage && [TemplateAvailabilityLevelTypes.ORGANIZATION, TemplateAvailabilityLevelTypes.RELYING_PARTY].includes(state.templateAvailabilityLevel);
    }, [session.user, isVericationServiceDetailsPage, state.templateAvailabilityLevel, state.company]);


    const resource = resources.modals.addMessagingTemplate;

    return (<div>
        <SelectFormGroup
            controlId={`messaging-template-template-availibility-level-select`}
            automationId={`messaging-template-template-availibility-level-select`}
            label={resource.inputs.templateAvailabilityLevel.label}
            ariaLabel={resource.inputs.templateAvailabilityLevel.label}
            onChange={handleUpdateTemplateAvailabilityLevel}
            options={possibleTemplateAvailabilityLevelOptions}
            selectedOption={selectedTemplateAvailabilityLevelOption}
            disabled={isDisabled}
            isRequired={true}
            isSearchable={false}
            isClearable={false}
            labelWidth={labelWidth}
        />
        {validationErrors.templateAvailabilityLevelError}

        {[TemplateAvailabilityLevelTypes.ORGANIZATION, TemplateAvailabilityLevelTypes.RELYING_PARTY].includes(state.templateAvailabilityLevel) &&
            <>
                <OrganizationSelectFormGroup
                    idPrefix={`add-messaging-template`}
                    onChange={handleUpdateCompany}
                    axnCompanies={axnCompanies}
                    selectedOption={selectedCompany}
                    disabled={axnCompanies.length === 1 || isDisabled || shouldDisableOrganizationUserOption}
                    isRequired={true}
                    isSearchable={true}
                    isClearable={axnCompanies.length > 1}
                    labelWidth={labelWidth}
                />

                {validationErrors.companyError}
            </>}

        {state.templateAvailabilityLevel === TemplateAvailabilityLevelTypes.RELYING_PARTY &&
            <>
                <RelyingPartySelectFormGroup
                    idPrefix={`add-messaging-template`}
                    onChange={handleUpdateRelyingParty}
                    axnRelyingParties={availableRelyingParties}
                    selectedOption={selectedRelyingParty}
                    disabled={axnRelyingParties.length === 1 || isDisabled || shouldDisableRelyingPartyOption}
                    isRequired={true}
                    isSearchable={true}
                    isClearable={axnRelyingParties.length > 1}
                    labelWidth={labelWidth}
                />
                {validationErrors.relyingPartyError}
            </>
        }

    </div>);
}